import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PP_LANGUAGE, PP_REGION } from "../constants/storage";
import { Cookie } from "../services/storage";
import { ApplicationState } from "../store";
import {
  actionSetGeoIPOverride,
  actionSetPrivacyLanguageRegion,
} from "../store/actions/privacy-actions";
import { LayoutState } from "../store/reducers/layout-reducer";
import { PrivacyState } from "../store/reducers/privacy-reducer";
import { UserState } from "../store/reducers/user-reducer";
import { LANGUAGE_VIEW } from "../types/countries";
import { UtilsLocale } from "../utils/UtilsLocale";
import { useCookie } from "./cookie-hooks";

export const usePrivacyPolicy = () => {
  const dispatch = useDispatch();
  const { isFCAllowed } = useCookie();

  const userState = useSelector<ApplicationState, UserState>(
    (state) => state.user
  );
  const privacyState = useSelector<ApplicationState, PrivacyState>(
    (state) => state.privacy
  );
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const { userCountryCode } = userState;
  const { isGeoIPOverride, region } = privacyState;

  useEffect(
    () => {
      // Override the current ppLanguage with GeoIP-derived language
      // if the user hasn't manually selected any language yet (isGeoIPOverride is true)
      if (userCountryCode !== "" && (isGeoIPOverride || region === "")) {
        const countryDataGeoIP = UtilsLocale.getCountryDataFromJson({
          countryCode: userCountryCode,
          languageView: LANGUAGE_VIEW.PP_LANGUAGE,
        });
        if (
          countryDataGeoIP &&
          Object.keys(countryDataGeoIP).length > 0 &&
          countryDataGeoIP.region !== "" &&
          countryDataGeoIP.languages?.length > 0 &&
          countryDataGeoIP.languages[0].code
        ) {
          const { languages, code, region } = countryDataGeoIP;
          // Prefer the language matching the current site language,
          // fall back to the first language if no match is found
          const matchedLang = languages.find(
            (lang) => lang.code === layoutState.languageCode
          );
          const langCode = matchedLang ? matchedLang.code : languages[0].code;
          updatePrivacyPolicyLanguage(`${langCode}-${code}`, region, true);
          disableGeoIPOverride();
        }
      }
    },
     
    [userCountryCode, isGeoIPOverride]
  );

  const updatePrivacyPolicyLanguage = (
    language: string,
    region: string,
    saveCookie = false
  ) => {
    if (saveCookie) {
      if (isFCAllowed) {
        Cookie.set(PP_REGION, region, { expires: 365 });
        Cookie.set(PP_LANGUAGE, language, { expires: 365 });
      }
    }

    dispatch(
      actionSetPrivacyLanguageRegion({
        language,
        region,
      })
    );
  };

  const disableGeoIPOverride = () => {
    dispatch(actionSetGeoIPOverride({ isGeoIPOverride: false }));
  };

  return {
    updatePrivacyPolicyLanguage,
    isGeoIPOverride,
    disableGeoIPOverride,
  };
};
